import React from 'react';

import { getAgentThreadRoute } from './agent/agentRoutes';
import { getAgentThreadRelativeAge, sortAgentThreads } from './agent/agentThreadPresentation';
import type { AgentThreadSummary } from './agent/protocol';

export const AGENT_RECENT_THREADS_LIMIT = 5;

/**
 * Picks the newest resumable conversations for the empty full-page canvas.
 *
 * The active thread is excluded: on the empty canvas it is the pristine conversation being
 * composed, so offering it as history would link the user to the page they are already on.
 */
export const selectRecentAgentThreads = (
  threads: readonly AgentThreadSummary[],
  activeThreadId: string
): AgentThreadSummary[] =>
  sortAgentThreads(threads)
    .filter((thread) => thread.externalThreadId !== activeThreadId)
    .slice(0, AGENT_RECENT_THREADS_LIMIT);

type Props = {
  activeThreadId: string;
  onSelectThread: (threadId: string) => void;
  threads: readonly AgentThreadSummary[];
  translate: (key: string, values?: Record<string, string | number>) => string;
};

/**
 * The five most recent conversations as links under the full-page new-chat composer.
 *
 * Real anchors keep middle-click and cmd-click working against the durable thread routes; a plain
 * left click stays in the SPA through the same thread-selection path the history menu uses.
 */
const AgentRecentThreads: React.FC<Props> = ({ activeThreadId, onSelectThread, threads, translate }) => {
  const recentThreads = selectRecentAgentThreads(threads, activeThreadId);
  if (recentThreads.length === 0) {
    return null;
  }
  const untitled = translate('hiring-dashboard-ai-assistant:agent.threads.untitled');
  const now = new Date();

  return (
    <nav
      aria-label={translate('hiring-dashboard-ai-assistant:agent.recentThreads.label')}
      data-testid="agent-recent-threads"
    >
      <ul className="flex flex-col items-center gap-1">
        {recentThreads.map((thread) => {
          const title = thread.title ?? untitled;
          const age = getAgentThreadRelativeAge(thread.updatedAt, now);
          const ageText = age ? translate(`common:relativeTime.${age.key}`, { val: age.value }) : '';
          return (
            <li
              className="flex w-full min-w-0 items-baseline justify-center gap-1.5 px-2 py-1 text-sm text-muted-foreground"
              key={thread.externalThreadId}
            >
              <a
                className="min-w-0 truncate rounded-sm underline underline-offset-4 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                href={getAgentThreadRoute(thread.externalThreadId)}
                onClick={(event) => {
                  // Modified and non-primary clicks keep native anchor behavior (new tab, etc.).
                  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
                    return;
                  }
                  event.preventDefault();
                  onSelectThread(thread.externalThreadId);
                }}
              >
                {title}
              </a>
              {ageText && (
                <>
                  <span aria-hidden>•</span>
                  <span className="whitespace-nowrap">{ageText}</span>
                </>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default AgentRecentThreads;
